import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowSquareOutIcon,
  ArrowsLeftRightIcon,
  CheckCircleIcon,
  LightningIcon,
  SignatureIcon,
  SpinnerIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { KeyValue } from '@/components/ui/key-value';

import {
  forwardWalletBatch,
  getSwapTypedData,
  type SwapTypedDataResponse,
} from '../api';
import { useAuth } from '../auth';
import { useT, type TFunction } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';
import { tryGetTelegram } from '../telegram';
import { getSigner } from '../wallet';

type Phase =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: SwapTypedDataResponse }
  | { kind: 'signing' }
  | { kind: 'submitting' }
  | { kind: 'submitted'; transactionHash: string; state: string };

const fmtUsd = (raw: string) => `$${(Number(raw) / 1e6).toFixed(2)}`;
const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;

/**
 * Mapeia `Error` cru de `request()` (`"<status> <detail>"`) numa mensagem
 * acionável. O backend devolve 400 "No USDC or USDC.e balance to swap…" como
 * caso esperado quando o usuário abre o Swap sem saldo — daí o CTA `Depositar`
 * em vez de stack trace.
 */
function classifyError(
  raw: string,
  t: TFunction,
): { title: string; body: string; showDeposit: boolean } {
  const m = raw.match(/^(\d{3})\s+(.*)$/);
  const status = m ? Number(m[1]) : 0;
  const detail = m ? m[2] : raw;
  if (status === 400 && /no usdc/i.test(detail)) {
    return {
      title: t('swap.no_balance_title'),
      body: t('swap.no_balance_body'),
      showDeposit: true,
    };
  }
  return { title: t('swap.failed_title'), body: detail, showDeposit: false };
}

export default function Swap() {
  const auth = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useTelegramBackButton();
  useNavStackEntry({ label: t('swap.breadcrumb') });

  useEffect(() => {
    if (auth.state !== 'ready') return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getSwapTypedData(auth.auth.externalUserId, {});
        if (!cancelled) setPhase({ kind: 'ready', data });
      } catch (err) {
        if (!cancelled)
          setPhase({ kind: 'error', message: (err as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.state, auth.state === 'ready' ? auth.auth.externalUserId : null]);

  async function onSign() {
    if (phase.kind !== 'ready' || auth.state !== 'ready') return;
    const { typedData, args } = phase.data;
    try {
      setPhase({ kind: 'signing' });
      const signer = await getSigner();
      const types = { ...typedData.types };
      delete (types as Record<string, unknown>)['EIP712Domain'];
      const signature = await signer.signTypedData(
        typedData.domain as never,
        types,
        typedData.message,
      );

      setPhase({ kind: 'submitting' });
      const resp = await forwardWalletBatch(auth.auth.externalUserId, {
        type: 'WALLET',
        from: args.from,
        to: args.factoryAddress,
        nonce: args.nonce,
        signature,
        depositWalletParams: {
          depositWallet: args.walletAddress,
          deadline: args.deadline,
          calls: args.calls,
        },
      });
      tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('success');
      setPhase({
        kind: 'submitted',
        transactionHash: resp.transactionHash,
        state: resp.state,
      });
    } catch (err) {
      tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('error');
      setPhase({ kind: 'error', message: (err as Error).message });
    }
  }

  if (auth.state === 'loading') {
    return (
      <AuthLoadingScreen
        title={t('swap.preparing_title')}
        description={t('swap.preparing_desc')}
        shortDescription={t('swap.preparing_short')}
      />
    );
  }
  if (auth.state === 'error') {
    return (
      <ScreenShell eyebrow={t('swap.eyebrow')} title={t('swap.title')}>
        <Alert tone="danger">
          <AlertTitle>{t('swap.auth_failed_title')}</AlertTitle>
          <AlertDescription>{auth.error}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }
  if (phase.kind === 'loading') {
    return (
      <AuthLoadingScreen
        title={t('swap.quoting_title')}
        description={t('swap.quoting_desc')}
        shortDescription={t('swap.quoting_short')}
        steps={[
          { label: t('swap.step_session'), color: 'bg-emerald-500' },
          { label: t('swap.step_wallet'), color: 'bg-sky-500', delay: '0.4s' },
          { label: t('swap.step_relayer'), color: 'bg-primary', delay: '0.8s' },
        ]}
      />
    );
  }

  if (phase.kind === 'error') {
    const err = classifyError(phase.message, t);
    return (
      <ScreenShell eyebrow={t('swap.eyebrow')} title={t('swap.title')}>
        <Alert tone="danger">
          <AlertTitle>{err.title}</AlertTitle>
          <AlertDescription>{err.body}</AlertDescription>
        </Alert>
        {err.showDeposit && (
          <Button size="lg" onClick={() => navigate('/deposit')}>
            {t('swap.cta_deposit')}
          </Button>
        )}
        <Button
          variant="outline"
          size="lg"
          onClick={() => tryGetTelegram()?.close?.()}
        >
          {t('common.back_to_chat')}
        </Button>
      </ScreenShell>
    );
  }

  if (phase.kind === 'submitted') {
    const url = phase.transactionHash
      ? `https://polygonscan.com/tx/${phase.transactionHash}`
      : null;
    return (
      <ScreenShell eyebrow={t('swap.eyebrow')} title={t('swap.submitted_title')}>
        <Card>
          <CardHeader>
            <CardEyebrow>
              <CheckCircleIcon weight="fill" className="size-3" />
              {t('swap.submitted_eyebrow')}
            </CardEyebrow>
            <CardTitle>{t('swap.submitted_body')}</CardTitle>
            <CardDescription>{t('swap.submitted_desc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <KeyValue
              label={t('swap.state_label')}
              value={<Badge tone="success">{phase.state}</Badge>}
            />
          </CardContent>
          <CardFooter>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className={buttonVariants({ variant: 'outline', size: 'lg' })}
              >
                <ArrowSquareOutIcon weight="bold" className="size-4" />
                {t('swap.view_polygonscan')}
              </a>
            )}
            <Button size="lg" onClick={() => tryGetTelegram()?.close?.()}>
              {t('common.back_to_chat')}
            </Button>
          </CardFooter>
        </Card>
      </ScreenShell>
    );
  }

  const data = phase.kind === 'ready' ? phase.data : null;
  const busy = phase.kind === 'signing' || phase.kind === 'submitting';

  return (
    <ScreenShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5">
          <ArrowsLeftRightIcon weight="fill" className="size-3" />
          {t('swap.eyebrow')}
        </span>
      }
      title={t('swap.title')}
      description={t('swap.description')}
    >
      <NavBar />
      <Card>
        <CardHeader>
          <CardEyebrow>
            <LightningIcon weight="fill" className="size-3" />
            {t('swap.quote_label')}
          </CardEyebrow>
          <CardTitle className="text-3xl tabular-nums sm:text-4xl">
            {data ? fmtUsd(data.amountIn) : '—'}
          </CardTitle>
          <CardDescription>{t('swap.usdc_on_polygon')}</CardDescription>
        </CardHeader>
        <CardContent>
          <KeyValue
            label={t('swap.from_label')}
            value={data ? `${fmtUsd(data.amountIn)} USDC` : '—'}
          />
          <KeyValue
            label={t('swap.to_min_label')}
            value={data ? `≥ ${fmtUsd(data.minOut)} pUSD` : '—'}
          />
          <KeyValue
            label={t('swap.wallet_label')}
            value={data ? short(data.args.walletAddress) : '—'}
            mono
          />
          <KeyValue
            label={t('swap.gas_label')}
            value={<Badge tone="success">{t('swap.gasless')}</Badge>}
          />
        </CardContent>
        <CardFooter>
          <Button size="lg" onClick={onSign} disabled={phase.kind !== 'ready'}>
            {busy ? (
              <SpinnerIcon weight="bold" className="size-4 animate-spin" />
            ) : (
              <SignatureIcon weight="bold" className="size-4" />
            )}
            {phase.kind === 'signing'
              ? t('swap.signing')
              : phase.kind === 'submitting'
                ? t('swap.submitting')
                : t('swap.cta_sign')}
          </Button>
        </CardFooter>
      </Card>
    </ScreenShell>
  );
}
